import DOMPurify, { type DOMPurify as DOMPurifyInstance } from 'dompurify';

/** The attribute Multiservice uses to mark an image embedded in an HTML comment. */
export const INLINE_ATTACHMENT_ATTRIBUTE = 'data-attachment-id';

/** The inline style properties kept on HTML comments, so images and tables keep their layout. */
const ALLOWED_STYLE_PROPERTIES = new Set(['width', 'height', 'text-align', 'vertical-align']);

/** Accepted values for width and height: a plain number of pixels or a percentage. */
const SIZE_VALUE = /^\d+(?:\.\d+)?(?:px|%)?$/;

/** Accepted values for text-align and vertical-align. */
const ALIGN_VALUE = /^(?:left|right|center|justify|top|middle|bottom|baseline)$/;

/** The sanitizer for HTML comments, created on first use so its hooks don't affect other callers. */
let purifier: DOMPurifyInstance | undefined;

/**
 * Keeps only the layout properties of an inline style (width, height and alignment) with plain
 * values, so a comment can size its images and tables but can't load URLs or restyle the page.
 * Returns an empty string when nothing is kept.
 */
export function filterSupportStyle(style: string): string {
  return style
    .split(';')
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator < 0) return '';
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim().toLowerCase();
      if (!ALLOWED_STYLE_PROPERTIES.has(property)) return '';
      const pattern = property === 'width' || property === 'height' ? SIZE_VALUE : ALIGN_VALUE;
      return pattern.test(value) ? `${property}: ${value}` : '';
    })
    .filter((declaration) => declaration !== '')
    .join('; ');
}

/**
 * Returns the sanitizer for HTML comments, which reduces inline styles to their layout properties.
 */
function getPurifier(): DOMPurifyInstance {
  if (!purifier) {
    purifier = DOMPurify(window);
    purifier.addHook('uponSanitizeAttribute', (_node, data) => {
      if (data.attrName !== 'style') return;
      data.attrValue = filterSupportStyle(data.attrValue);
      if (!data.attrValue) data.keepAttr = false;
    });
  }
  return purifier;
}

/**
 * Sanitizes an HTML comment from support staff or the AI assistant. Keeps formatting, links, tables,
 * inline images and their sizes, but drops scripts, event handlers, styling other than layout and
 * any image source: inline image bytes are loaded through the authenticated attachment endpoint,
 * never from a URL in the comment.
 */
export function sanitizeSupportHtml(html: string): string {
  return getPurifier().sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'span', 'div',
      'strong', 'b', 'em', 'i', 'u', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'code', 'pre', 'blockquote', 'hr',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col',
      'img',
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'width', 'height', 'colspan', 'rowspan', 'style', INLINE_ATTACHMENT_ATTRIBUTE,
    ],
    // Sizes and spans are plain numbers, so they skip the URI check that would otherwise drop them.
    ADD_URI_SAFE_ATTR: ['width', 'height', 'colspan', 'rowspan'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
  });
}

/**
 * Returns the IDs of the inline images referenced by an HTML comment, in order.
 */
export function getInlineAttachmentIds(html: string): string[] {
  const ids: string[] = [];
  const pattern = new RegExp(`${INLINE_ATTACHMENT_ATTRIBUTE}\\s*=\\s*["']([^"']+)["']`, 'gi');
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(html)) !== null) {
    if (!ids.includes(match[1])) ids.push(match[1]);
  }
  return ids;
}
